import useSWR from "swr";
import { type CityListResponse } from "../types/city";
import { useCallback, useEffect, useState } from "react";
import { useApiClient } from "./useApiClient";

interface CityResponse {
  city: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  cityList: CityListResponse;
}

// 指定したprefCodeから市区町村の一覧を取得するcustom hook
export const useCity = (
  prefCode: string,
  initialCityName: string
): CityResponse => {
  const { apiClient } = useApiClient();

  // 市区町村セレクトボックス
  const [cityCode, setCityCode] = useState<string>("");

  // swrに引き渡すfetcher
  const fetcher = async (url: string): Promise<CityListResponse> => {
    const { data } = await apiClient.get<CityListResponse>(url);
    return data;
  };

  const { data: cityList } = useSWR<CityListResponse>(
    prefCode === "" ? null : `/general/city?pref_code=${prefCode}`,
    fetcher,
    { revalidateOnFocus: false }
  );

  const getCityCodeByCityName = useCallback(
    (cityName: string): string => {
      if (cityList)
        return (
          cityList.list.find((city) => city.name === cityName)?.cityCode ?? ""
        );
      return "";
    },
    [cityList]
  );

  useEffect(() => {
    setCityCode(getCityCodeByCityName(initialCityName));
  }, [cityList]);

  return {
    city: cityCode,
    setCity: setCityCode,
    cityList: cityList ?? { list: [] },
  };
};

interface CityWithExternalStateResponse {
  cityList: CityListResponse;
  getCityCodeByCityName: (cityName: string) => string;
  getCityNameByCityCode: (cityCode: string) => string;
}

/**
 * 外部でcityCodeを管理するバージョンのuseCity
 * - react hook formなど外部でcityCodeを管理する場合に使用する
 *
 * @params
 * - prefCode {{ a: string, b: number }} 都道府県コード
 * - initialCityName cityCodeを知りたいcityNameを初期値として指定する
 * - onInitialCityLoad initialCityNameで設定したcityCodeを取得した後に実行するコールバック関数
 * @returns
 * - cityList: 指定したprefCodeの市町村の一覧
 * - getCityCodeByCityName: (cityName: string) => string;
 * - getCityNameByCityCode: (cityCode: string) => string;
 */
export const useCityWithExternalState = (
  prefCode: string,
  initialCityName?: string,
  onInitialCityLoad?: (cityCode: string) => void
): CityWithExternalStateResponse => {
  const { apiClient } = useApiClient();

  // swrに引き渡すfetcher
  const fetcher = async (url: string): Promise<CityListResponse> => {
    const { data } = await apiClient.get<CityListResponse>(url);
    return data;
  };

  const { data: cityList } = useSWR<CityListResponse>(
    prefCode === "" ? null : `/general/city?pref_code=${prefCode}`,
    fetcher,
    { revalidateOnFocus: false }
  );

  const getCityCodeByCityName = useCallback(
    (cityName: string): string => {
      if (cityList)
        return (
          cityList.list.find((city) => city.name === cityName)?.cityCode ?? ""
        );
      return "";
    },
    [cityList]
  );

  const getCityNameByCityCode = useCallback(
    (cityCode: string): string => {
      if (cityList)
        return (
          cityList.list.find((city) => city.cityCode === cityCode)?.name ?? ""
        );
      return "";
    },
    [cityList]
  );

  // 初期値が指定されている場合、対応するcityCodeを返す
  useEffect(() => {
    if (initialCityName && cityList && onInitialCityLoad) {
      const cityCode = getCityCodeByCityName(initialCityName);
      onInitialCityLoad(cityCode);
    }
  }, [cityList, initialCityName, onInitialCityLoad, getCityCodeByCityName]);

  return {
    cityList: cityList ?? { list: [] },
    getCityCodeByCityName,
    getCityNameByCityCode,
  };
};
